import { ApiError } from '@/teoCore/errors/ApiError';

import {
  type APITeoMarker,
  type MarkerColor,
  type WordListData,
  type WordListEntry,
  type WordListExpression,
  type WordListScanRequest,
  type WordListScanResult,
} from '../../TeoApi';
import { TeoEventTarget } from '../../utility/TeoEventTarget';
import type { TeoText } from '../TeoText';
import { BrandFitHandler } from './BrandFitHandler';
import { DictionaryHandler } from './DictionaryHandler';
import { WhiteListHandler } from './WhiteListHandler';

export type WordListHandlerEvents = {
  scanPending: (detail: boolean) => void;
  gotScanResult: (detail: WordListScanResult) => void;
  entryMarkerClicked: (relation: string) => void;
};

export type UiWordListSuggestion = {
  key: string;
  suggestion: string;
  variant: string;
};

export type UiWordListEntry = {
  key: string;
  wordListKey: string;
  cardLabel: string;
  contentText?: string;
  improvementsLabel?: string;
  suggestions: UiWordListSuggestion[];
  variant: string;
  variantLabel: string;
  // markerRelation: number;
  checkedText?: string;
  onCheckedClick?: (entryKey: string, checked: boolean) => void;
  infoText?: string;
  onInfoClick?: () => void;
};

type RelationType = {
  variant: string;
  cardLabel: string;
  improvementsLabel?: string;
  markerPriorities: number[];
};

const relationTypes: Record<number, RelationType> = {
  1: {
    variant: 'info',
    cardLabel: 'Begriff',
    improvementsLabel: 'Synonyme:',
    markerPriorities: [0, 1, 2],
  },
  2: {
    variant: 'phrases',
    cardLabel: 'Formulierung',
    improvementsLabel: 'Synonyme:',
    markerPriorities: [0],
  },
  3: {
    variant: 'wording',
    cardLabel: 'Schreibweise',
    improvementsLabel: 'Ersetze mit:',
    markerPriorities: [0],
  },
  //4: white list
  5: {
    variant: 'recommendation',
    cardLabel: 'Überprüfe',
    markerPriorities: [0],
  },
};

const wordListColors: Record<string, MarkerColor> = {
  'corporate-text-pruefung': 'warning',
  'corporate-schreibweisen': 'error',
  'weitere-regeln': 'warning',
};

export class WordListHandler {
  public events = new TeoEventTarget<WordListHandlerEvents>();
  public markers: APITeoMarker[] = [];
  public brandFit: BrandFitHandler;
  public dictionary: DictionaryHandler;
  public whiteList: WhiteListHandler;
  // private lastScanResult: WordListScanResult | null = null;
  private ignoredEntries: {
    word_list_id: number;
    entry_id: number;
  }[] = [];
  // private markerClickRemover: (() => void) | null = null;
  constructor(private text: TeoText) {
    this.brandFit = new BrandFitHandler(this, text);
    this.dictionary = new DictionaryHandler(this, text);
    this.whiteList = new WhiteListHandler(this, text);
    this.text.markers.events.addEventListener(
      'markerClicked',
      ((detail: { markerId: number; marker: APITeoMarker }) => {
        this.handleMarkerClick(detail);
      }).bind(this),
    );
  }

  handleMarkerClick(detail: { markerId: number; marker: APITeoMarker }): void {
    if (detail.marker.groupName !== 'wordlist') {
      return;
    }
    this.events.triggerEvent('entryMarkerClicked', detail.markerId.toString());
  }

  getWordListVariant(wordList: WordListData): string {
    const type = wordList.relation_type;
    if (relationTypes[type]?.variant) {
      return relationTypes[type].variant;
    }
    return 'phrases';
  }

  getWordListEntryLabel(wordList: WordListData): string {
    const type = wordList.relation_type;
    if (relationTypes[type]?.cardLabel) {
      return relationTypes[type].cardLabel;
    }
    return 'TODO';
  }

  getWordListEntryImprovementsLabel(wordList: WordListData): string | undefined {
    return relationTypes[wordList.relation_type]?.improvementsLabel;
  }

  createSuggestions(entry: WordListEntry): UiWordListSuggestion[] {
    const expressions: WordListExpression[] = entry.expressions;
    const suggestions = expressions
      .filter((e) => e.priority > 0)
      .slice()
      .sort((a, b) => a.priority - b.priority)
      .map((e) => ({
        key: e.id.toString(),
        suggestion: e.full_expression,
        variant: 'suggestion',
      }));
    if (suggestions.length === 1 && suggestions[0]!.key == entry.found_by.toString()) {
      // If the only suggestion is the found_by expression, we don't need to show it.
      return [];
    }
    return suggestions;
  }

  replaceInText(relation: number, suggestion: UiWordListSuggestion): void {
    this.text.markers.replaceMarkerContent(relation, suggestion.suggestion);
  }

  createUiWordListEntry(entry: WordListEntry, wordList: WordListData): UiWordListEntry {
    const variant = this.getWordListVariant(wordList);
    const improvementsLabel = this.getWordListEntryImprovementsLabel(wordList);
    return {
      key: entry.current_relation.toString(),
      wordListKey: wordList.id.toString(),
      cardLabel: this.getWordListEntryLabel(wordList),
      improvementsLabel,
      contentText: !improvementsLabel ? entry.description : undefined,
      suggestions: this.createSuggestions(entry),
      variant,
      variantLabel: entry.found_string || entry.title || '-',
      checkedText: variant == 'recommendation' ? 'Geprüft' : undefined,
      // markerRelation: entry.current_relation,
      onCheckedClick: (entryKey: string, checked: boolean) => {
        this.checkIgnoreEntry(wordList.id, entry.id, checked);
      },
      infoText: variant !== 'recommendation' && entry.description ? 'Erklärung' : undefined,
      onInfoClick:
        entry.description ?
          () => {
            this.openDescription(entry);
          }
        : undefined,
    };
  }

  private openDescription(entry: WordListEntry): void {
    this.text.core.notifications.add({
      mode: 'info',
      title: 'Erklärung: ' + entry.title,
      message: entry.description,
      buttons: [
        {
          label: 'Schließen',
        },
      ],
    });
  }

  checkIgnoreEntry(wordListId: number, entryId: number, checked: boolean): void {
    const isChecked = this.ignoredEntries.some(
      (e) => e.word_list_id === wordListId && e.entry_id === entryId,
    );
    if (checked && !isChecked) {
      this.ignoredEntries.push({ word_list_id: wordListId, entry_id: entryId });
    } else if (!checked && isChecked) {
      this.ignoredEntries = this.ignoredEntries.filter(
        (e) => !(e.word_list_id === wordListId && e.entry_id === entryId),
      );
    }
    this.text.touch();
  }

  getWordListMarkerNames(wordList: WordListData): string[] {
    const names: string[] = [];
    let markerPriorities: number[] = [0, 1, 2];
    if (relationTypes[wordList.relation_type]?.markerPriorities) {
      markerPriorities = relationTypes[wordList.relation_type]!.markerPriorities;
    }
    markerPriorities.forEach((priority) => {
      names.push(`${wordList.name}-prio-${priority}`);
    });
    return names;
  }

  getWordListFoundEntries(wordList: WordListData): WordListEntry[] {
    const markerPriorities = relationTypes[wordList.relation_type]?.markerPriorities ?? [0, 1, 2];
    return wordList.current_text_entries.filter((entry) =>
      markerPriorities.includes(
        entry.expressions.find((expression) => expression.id == entry.found_by)?.priority ?? -1,
      ),
    );
  }

  mark(lists: WordListData[], clickable = true): void {
    const markerNames: string[] = [];
    lists.forEach((wordList) => {
      const names = this.getWordListMarkerNames(wordList);
      markerNames.push(...names);
    });
    this.setMarkers(markerNames, clickable);
  }

  setMarkers(markerNames: string[], clickable = true): void {
    if (clickable) {
      this.text.markers.setMarkers([], markerNames);
    } else {
      this.text.markers.setMarkers(markerNames);
    }
  }

  private getMarkerColor(markerName: string): MarkerColor {
    for (const [key, color] of Object.entries(wordListColors)) {
      if (markerName.startsWith(key)) {
        return color;
      }
    }
    return 'neutral';
  }

  async scan() {
    const textArray = this.text.getTextArray?.();
    if (!textArray) {
      console.error('No text array available for scanning.');
      return;
    }
    this.events.triggerEvent('scanPending', true);
    const request: WordListScanRequest = {
      text: textArray,
      ignored_entries: this.ignoredEntries,
    };

    const response = await this.text.core.api.getWordListScan(request);
    if (ApiError.matches(response)) {
      console.error('Error in word list scan:', response);
      this.events.triggerEvent('scanPending', false);
      return;
    }
    // this.lastScanResult = response;
    this.markers = [];
    response.markers.forEach((marker) => {
      this.markers.push({
        ...marker,
        groupName: 'wordlist',
        color: marker.color || this.getMarkerColor(marker.marker),
      });
    });
    this.events.triggerEvent('gotScanResult', response);
    this.events.triggerEvent('scanPending', false);
  }
}
